import axios from "axios";
import { get, post } from "../../../services/api";
import { newRequestKey } from "../workorders/workOrdersApi";
import { MaintApiError, MaintContext, MaintDetail, MaintList, MaintSession, MaintVendor } from "./types";

export { newRequestKey };

/** Every failure becomes { status, code, message, details } the screens can branch on. */
export const toMaintError = (error: any): MaintApiError => {
  const status = Number(error?.response?.status) || 0;
  const payload = error?.response?.data?.payload || {};
  return {
    status,
    code: String(payload.code || (status ? "HTTP_" + status : "NETWORK")),
    message: String(
      payload.error ||
        error?.response?.data?.messages ||
        (status ? "Something went wrong. Please try again." : "No connection. Check your network and try again.")
    ),
    details: payload.details,
  };
};

const call = async <T,>(request: Promise<any>): Promise<T> => {
  try {
    const response = await request;
    return response.payload as T;
  } catch (error) {
    throw toMaintError(error);
  }
};

const id = (value: string) => encodeURIComponent(value);
const query = (params: Record<string, any>) => {
  const parts = Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== "")
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`);
  return parts.length ? `?${parts.join("&")}` : "";
};
const wo = (publicId: string, action: string) => `maintenance/work-orders/${id(publicId)}/${action}`;

export const maintenanceApi = {
  session: () => call<MaintSession>(get("maintenance/session")),
  context: () => call<MaintContext>(get("maintenance/context")),
  dashboard: () => call<Record<string, any>>(get("maintenance/dashboard")),
  list: (params: Record<string, any>) => call<MaintList>(get(`maintenance/work-orders${query(params)}`)),
  history: (params: Record<string, any>) => call<any>(get(`maintenance/history${query(params)}`)),
  invoices: (params: Record<string, any>) => call<any>(get(`maintenance/invoices${query(params)}`)),
  get: (publicId: string) => call<MaintDetail>(get(`maintenance/work-orders/${id(publicId)}`)),
  quotes: (publicId: string) => call<any>(get(wo(publicId, "quotes"))),
  submit: (body: any) => call<MaintDetail>(post("maintenance/work-orders", body)),
  command: (publicId: string, action: string, body: any) => call<MaintDetail>(post(wo(publicId, action), body)),
  quoteRequestCommand: (publicId: string, requestId: string, action: "cancel" | "extend", body: any) =>
    call<MaintDetail>(post(`maintenance/work-orders/${id(publicId)}/quote-requests/${id(requestId)}/${action}`, body)),
  vendors: (params: Record<string, any> = {}) => call<{ vendors: MaintVendor[] }>(get(`maintenance/vendors${query(params)}`)),
  vendor: (vendorId: string) => call<{ vendor: MaintVendor; history: any[] }>(get(`maintenance/vendors/${id(vendorId)}`)),
  createVendor: (body: any) => call<any>(post("maintenance/vendors", body)),
  updateVendor: (vendorId: string, body: any) => call<any>(post(`maintenance/vendors/${id(vendorId)}/update`, body)),
  createUpload: (body: any) => call<{ upload_id: string; upload_url: string; headers: Record<string, string> }>(post("maintenance/uploads", body)),
  finalizeUpload: (uploadId: string) => call<any>(post(`maintenance/uploads/${id(uploadId)}/finalize`, {})),
  download: (attachmentId: string) => call<{ url: string }>(get(`maintenance/attachments/${id(attachmentId)}/download`)),
  settings: () => call<any>(get("maintenance/settings")),
  updateSettings: (body: any) => call<any>(post("maintenance/settings", body)),
  saveArea: (body: any) => call<any>(post("maintenance/areas", body)),
  saveCategory: (body: any) => call<any>(post("maintenance/categories", body)),
  saveLocation: (storeId: number, body: any) => call<any>(post(`maintenance/locations/${storeId}`, body)),
  inviteMember: (body: any) => call<any>(post("maintenance/members/invite", body)),
  updateMember: (userId: number, body: any) => call<any>(post(`maintenance/members/${userId}/update`, body)),
};

export type MaintenanceApi = typeof maintenanceApi;

/**
 * The sign-in bootstrap for a maintenance-only account (role 11): the session
 * from its own access token, before anything is in localStorage. Uses a plain
 * request so the app's interceptors (which read localStorage) stay out of it.
 */
export const fetchSessionWithToken = async (accessToken: string): Promise<MaintSession> => {
  try {
    const response = await axios.create({ baseURL: process.env.REACT_APP_BASE_API_URL }).get("maintenance/session", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data.payload as MaintSession;
  } catch (error) {
    throw toMaintError(error);
  }
};

// ---------------------------------------------------------------------------
// The shell's "is maintenance available to me?" (drives the nav entry)
// ---------------------------------------------------------------------------

const CACHE_KEY = "pn.maintenanceSession";
const CACHE_MS = 15 * 60 * 1000;
let cached: MaintSession | null | undefined;
let pending: Promise<void> | null = null;
const listeners = new Set<() => void>();

const readCache = (): MaintSession | null | undefined => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return Date.now() - Number(parsed.at) < CACHE_MS ? parsed.session : undefined;
  } catch (_) {
    return undefined;
  }
};

/** Loads (once per page load, cached for 15 minutes) whether this person has maintenance. Never throws. */
export const loadMaintenanceAccess = (api: Pick<MaintenanceApi, "session"> = maintenanceApi) => {
  if (cached !== undefined) return Promise.resolve();
  const fromCache = readCache();
  if (fromCache !== undefined) {
    cached = fromCache;
    return Promise.resolve();
  }
  if (!pending) {
    pending = api
      .session()
      .then((session) => {
        cached = session;
      })
      .catch(() => {
        cached = null;
      })
      .then(() => {
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), session: cached }));
        } catch (_) {
          /* storage unavailable: just not cached */
        }
        listeners.forEach((listener) => listener());
      });
  }
  return pending;
};

export const subscribeMaintenanceAccess = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const maintenanceCapabilities = (): string[] => (cached && cached.maintenance ? cached.maintenance.capabilities : []);
export const hasMaintenanceAccess = () => maintenanceCapabilities().length > 0;

/** For tests and sign-out. */
export const resetMaintenanceAccess = () => {
  cached = undefined;
  pending = null;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (_) {
    /* nothing stored */
  }
};
